import React, { useEffect, useRef, useState } from "react";
import s from "./synchronized-audio-player.module.scss";
import { toast } from 'react-toastify';
import { Tooltip } from 'react-tooltip';
import { useDropzone } from "react-dropzone";
import { IconCloudUpload, IconTrash, IconVolume2 } from "@tabler/icons-react";
import { RHAP_UI } from "react-h5-audio-player";
import classNames from "classnames";
import { useAudioControls, JUKEBOX_KINDS } from "../audio-controls-provider";
import { JukeboxVolumeControl } from "../jukebox-volume-control/jukebox-volume-control";
import { Loader } from "@shared/ui/loader/loader";
import { AudioPlayer } from "../audio-player/audio-player";
import { useWebAudioContext } from "@shared/context/WebAudio";

/** Пояснение для блокировки запуска при выключенном микрофоне. */
const MIC_OFF_TOOLTIP =
	'Включите микрофон: музыка идёт собеседнику через аудиотрек занятия, без микрофона её не слышно';
const MIC_OFF_TOOLTIP_ID = 'tooltip-sync-audio-mic-player';

/** Порог: если элемент разошёлся с часами AudioContext больше чем на это — подстраиваем (с). */
/** Порог: скачок currentTime элемента дальше этого от движения 1:1 со временем = seek (с). */
const SEEK_JUMP_THRESHOLD_SEC = 0.3;
/** После рестарта потока не проверяем N мс, чтобы он «успокоился». */
const SETTLE_AFTER_RESTART_MS = 2000;

export const SynchronizedAudioPlayer = () => {
	const {
		someoneElsePlayingJukeBox,
		startMusic,
		stopMusic,
		audio,
		setAudio,
		jukeBoxKind,
		remoteSync,
		micEnabled,
	} = useAudioControls()
	const audioContext = useWebAudioContext()
	// Громкость локального мониторинга музыки. У слушателя отдельной
	// регулировки музыки нет: она смикширована с голосом ведущего в один трек
	const volumeKind = JUKEBOX_KINDS.music;

  const audioPlayerRef = useRef(null);
	const [isLoad, setIsLoad] = useState(false)
	// Единый источник позиции — часы AudioContext (звук в эфире идёт из них),
	// приглушённый <audio>-элемент лишь декодирует и показывает UI.
	// startPos/startedAt — момент старта потока; программные правки currentTime
	// помечаются флагом, чтобы не спутать с пользовательским сиком.
	const streamClockRef = useRef({ startPos: 0, startedAt: 0 });

	const restartFromPosition = (positionSec) => {
		if (micEnabled === false) return; // без микрофона трансляция невозможна
		streamClockRef.current = { startPos: positionSec, startedAt: 0 };
		startMusic(positionSec, undefined, false, JUKEBOX_KINDS.music, {
			fileName: audio?.name || audio?.path,
		}).then((startedAt) => {
			if (startedAt !== null && startedAt !== undefined) {
				streamClockRef.current.startedAt = startedAt;
			}
		});
	};

	// Ловим ПОЛЬЗОВАТЕЛЬСКИЙ seek: элемент мьютирован и идёт 1:1 со временем,
	// поэтому любой скачок currentTime — это seek по прогресс-бару.
	// Сравнивать элемент с позицией потока НЕЛЬЗЯ: поток стартует с запасом
	// scheduleDelayMs (≈250 мс), элемент легально впереди ровно на этот
	// запас — прежняя проверка по расхождению перезапускала поток каждую
	// секунду (музыка «икала»).
	useEffect(() => {
		const last = { wallTime: 0, elTime: 0 };
		let settleUntil = 0;
		const id = setInterval(() => {
			const el = audioPlayerRef.current?.audio?.current;
			const clock = streamClockRef.current;
			const now = Date.now();
			if (!el || !clock.startedAt || el.paused || now < settleUntil) {
				last.wallTime = 0;
				return;
			}
			if (last.wallTime) {
				const wallDeltaSec = (now - last.wallTime) / 1000;
				const elDeltaSec = el.currentTime - last.elTime;
				if (Math.abs(elDeltaSec - wallDeltaSec) > SEEK_JUMP_THRESHOLD_SEC) {
					// Элемент мьютирован — его позиция (куда seek'ил пользователь) — источник правды для рестарта
					last.wallTime = 0;
					settleUntil = now + SETTLE_AFTER_RESTART_MS;
					restartFromPosition(el.currentTime);
					return;
				}
			}
			last.wallTime = now;
			last.elTime = el.currentTime;
		}, 1000);
		return () => clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps -- refs и стабильные колбэки
	}, [audioContext, startMusic]);

  const { getRootProps, getInputProps } = useDropzone({
		disabled: isLoad,
		onDrop: (acceptedFiles) => {
			setIsLoad(true)
				const files = acceptedFiles.map(file => ({
					...file,
					preview: URL.createObjectURL(file),
					relativePath: file.webkitRelativePath || null,
				}))

				if(files?.length > 0){
					setAudio(files[0])
					setIsLoad(false)
				}else{
					setIsLoad(false)
				}
		}
  });

	if(isLoad){
		return <div className={s.syncPlayerWrapper}>
			<Loader/>
		</div>
	}

	if (someoneElsePlayingJukeBox) {
		return (
			<div className={classNames(s.syncPlayerWrapper, s.occupied)}>
				<div className={s.occupiedContent}>
					<IconVolume2 size={24} className={s.occupiedIcon} />
					<p className={s.occupiedText}>
						{jukeBoxKind === JUKEBOX_KINDS.metronome
							? 'Сейчас играет метроном собеседника'
							: `Сейчас играет: ${remoteSync?.fileName || 'аудио собеседника'}`}
					</p>
					<RemoteTrackProgress />
				</div>
			</div>
		);
	}

  return (
    <>
      <Tooltip id={MIC_OFF_TOOLTIP_ID} content={MIC_OFF_TOOLTIP} />
      {audio ? (
        <div
          className={classNames(s.syncPlayerWrapper, isLoad ? s.disabled : '')}
          data-tooltip-id={micEnabled === false ? MIC_OFF_TOOLTIP_ID : undefined}
        >
					<JukeboxVolumeControl kind={volumeKind} className={s.playerVolume} />
          <AudioPlayer
            refName={audioPlayerRef}
            fullWidth
            className={s.player}
						muted={true}
            layout="stacked-reverse"
						disabled={isLoad}
            customControlsSection={[
              RHAP_UI.MAIN_CONTROLS,
              <p className={s.playerName}>{audio.path.startsWith('./') ? audio.path.slice(2) : audio.path}</p>,
              <button onClick={() => {
								setIsLoad(true)
								setAudio(null)
								setIsLoad(false)
							}} className={s.removeBtn}>
                <IconTrash size={18} color={"#F12828"} />
              </button>
            ]}
            src={
              audio.preview
            }
            onPlay={() => {
						if (micEnabled === false) {
							// без микрофона трансляция невозможна — возвращаем элемент в стоп
							audioPlayerRef.current?.audio?.current?.pause?.();
							toast.info(MIC_OFF_TOOLTIP);
							return;
						}
							const position = audioPlayerRef.current?.audio?.current?.currentTime ?? 0;
							streamClockRef.current = { startPos: position, startedAt: 0 };
							startMusic(position, undefined, false, JUKEBOX_KINDS.music, {
								fileName: audio.name || audio.path,
							}).then((startedAt) => {
								if (startedAt !== null && startedAt !== undefined) {
									streamClockRef.current.startedAt = startedAt;
								}
							});
						}}
            onPause={() => {
							streamClockRef.current.startedAt = 0;
							stopMusic()
						}}
          />
        </div>
      ) : (
				<div {...getRootProps({ className: classNames(s.dropzoneWrapper, isLoad ? s.disabled : '') })}>
					<input {...getInputProps()} />
					<div className={s.dropzoneIcon}>
						<IconCloudUpload size={14} />
					</div>
					<div className={s.dropzoneText}>
						Выберите аудиофайл для воспроизведения
					</div>
				</div>
      )}
    </>
  );
};

/** Прогресс трека у слушателя: offset из sync-сообщения + время от wallStart. */
const RemoteTrackProgress = () => {
	const { remoteSync } = useAudioControls();
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 1000);
		return () => clearInterval(id);
	}, []);

	if (remoteSync?.action !== 'start' || typeof remoteSync.offset !== 'number') return null;
	const elapsed = Math.max(0, (now - remoteSync.wallStart) / 1000 + remoteSync.offset);
	const mm = Math.floor(elapsed / 60);
	const ss = Math.floor(elapsed % 60).toString().padStart(2, '0');
	return <p className={s.occupiedText}>{mm}:{ss}</p>;
};
